'use client';

import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';

import { cn } from '../lib/utils';

import { type Variant, loadingBar, loadingBarBg } from './tokens';

type Props = {
  /** Drives the bar's color so it stays legible against the button's variant
   *  surface (white/20 on filled dark, primary-alpha tints on light, etc.). */
  variant: Variant;
};

// Skip the bar entirely on actions that resolve before the user could see it
// — a 50ms flash reads as a glitch.
const SHOW_DELAY_MS = 150;

/**
 * Animated bar shown during the loading state. The parent decides when to
 * mount/unmount this component (typically inside an `AnimatePresence` so the
 * fade-out plays). Internally the bar waits 150ms before fading in to skip
 * flashes on fast Promises.
 */
export const LoadingBar = ({ variant }: Props) => {
  const [isShown, setIsShown] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsShown(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  if (!isShown) {
    return null;
  }

  return (
    <motion.span
      aria-hidden="true"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className={cn(loadingBar, loadingBarBg[variant])}
      style={{ zIndex: -1 }}
    />
  );
};
